import {useQueryClient} from '@tanstack/react-query';
import React, {useCallback} from 'react';
import formatMessageDate from '@/utils/formatMessageDate';
import {
  ROUTES,
  ACTIVITY_FEED_QUERY_PARAM,
  ACTIVITY_FEED_CREATOR_VALUE,
} from '@/config/routesConstants';
import type {UnifiedFeedItem} from '@/types/conversationMessage';
import AssetThreadCard from '@/components/asset-thread-card';
import NegotiationSnapshotDisplay from '@/components/conversation-chat/NegotiationSnapshotDisplay';
import MessageCard from '@/components/message-card';
import {unifiedMessagesQueryKey} from '@/hooks/queries/use-unified-messages/useUnifiedMessages';

type UnifiedFeedItemViewProps = {
  item: UnifiedFeedItem;
  conversationId: string;
  isLoadingAttachments?: boolean;
  onMarkUnread?: (messageId: string) => void;
};

function UnifiedFeedItemView({
  item,
  conversationId,
  isLoadingAttachments = false,
  onMarkUnread,
}: UnifiedFeedItemViewProps) {
  const queryClient = useQueryClient();

  const handleWithdrawSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: unifiedMessagesQueryKey(conversationId),
    });
  }, [queryClient, conversationId]);

  if (item.kind === 'version_thread') {
    const {campaignId, contentSubmissionId} = item.data;
    return (
      <AssetThreadCard
        thread={item.data}
        assetUrl={`${ROUTES.CAMPAIGNS_V2}/${campaignId}/assets/${contentSubmissionId}?${ACTIVITY_FEED_QUERY_PARAM}=${ACTIVITY_FEED_CREATOR_VALUE}`}
        onMarkUnread={onMarkUnread}
      />
    );
  }
  if (item.kind === 'negotiation_snapshot') {
    return (
      <NegotiationSnapshotDisplay
        snapshot={item.data.negotiationSnapshot}
        onWithdrawSuccess={handleWithdrawSuccess}
      />
    );
  }
  const msg = item.data;
  const {campaignId} = msg;
  const displayName = msg.sender?.name || msg.signerDisplayName;
  return (
    <MessageCard
      messageId={msg.id}
      displayName={displayName}
      avatarUrl={msg.sender?.imageUrl}
      formattedDate={formatMessageDate(msg.createdAt)}
      body={msg.body}
      bodyHtml={msg.bodyHtml}
      negotiationSnapshot={msg.negotiationSnapshot}
      attachments={msg.messageAttachments}
      isLoadingAttachments={isLoadingAttachments}
      isBrand={msg.sender?.kind === 'brand'}
      campaignName={msg.campaignName}
      campaignUrl={
        campaignId ? `${ROUTES.CAMPAIGNS_V2}/${campaignId}/creators` : undefined
      }
      onMarkUnread={onMarkUnread}
    />
  );
}

export default UnifiedFeedItemView;
